/* eslint-disable prettier/prettier */
import { createContext, useContext, useEffect, useState } from 'react';
import { AuthService } from '../services/auth.service';
import { useNavigate } from 'react-router-dom';

interface Role {
  id: string;
  name: string;
  description?: string | null;
  permissions: any[];
  isDefault: boolean;
  isActive: boolean;
}

interface Company {
  id: string;
  name: string;
  country: string;
  isActive: boolean;
  cedantsFileUrl?: string | null;
  brokersFileUrl?: string | null;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  profilePic: string | null;
  role: Role;
  company: Company | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const login = async (email: string, password: string) => {
    localStorage.removeItem('token');
    setUser(null);
    const res = await AuthService.login(email, password);
    localStorage.setItem('token', res.data.access_token);
    setUser(res.data.user);
    navigate('/');
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    AuthService.getMe()
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('token');
        setUser(null);
      })
      .finally(() => setLoading(false));

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token') {
        if (!event.newValue) {
          setUser(null);
          navigate('/login');
        } else {
          AuthService.getMe()
            .then((res) => {
              setUser(res.data);
              if (window.location.pathname === '/login') {
                navigate('/');
              }
            })
            .catch(() => {
              localStorage.removeItem('token');
              setUser(null);
              navigate('/login');
            });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
